import { useGlobal } from "@/app/providers/use-global";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import Icon from "@/components/ui/icon";
import type { PromptAndResponse } from "@/hooks/use-send-message";
import { cn } from "@/lib/utils";
import { Check, SlidersHorizontal } from "lucide-react";
import {
  type Dispatch,
  type RefObject,
  type SetStateAction,
  memo,
  useCallback,
  useState,
} from "react";
import { toast } from "sonner";
import {
  dropdownAndSelectContentItemStyles,
  dropdownContentStyles,
} from "../shared";
import type { useSendMessage } from "@/hooks/use-send-message";

interface PromptDropdownProps {
  chat: PromptAndResponse[];
  setChat: Dispatch<SetStateAction<PromptAndResponse[]>>;
  setIsLoadingMessage: Dispatch<SetStateAction<boolean>>;
  setInputBuffer: Dispatch<SetStateAction<string>>;
  sendMessage: ReturnType<typeof useSendMessage>["sendMessage"];
  scrollChatListBottom: RefObject<HTMLDivElement>;
  scrollChatListTop: RefObject<HTMLDivElement>;
}

enum OptionButtonsPrompt {
  Expand = "Rewrite with more details.",
  Bulletize = "Bulletize with asterisks.",
  Sentence = "Summarize in only 1 sentence.",
  Professional = "Improve with a professional and formal tone.",
  Educator = "Rewrite as if from a mentor/educator.",
  Friendly = "Rewrite with a friendly tone.",
  Neutral = "Rewrite as neutral and non expressive tone.",
}

const PromptDropdown = memo(
  ({
    chat,
    setChat,
    setIsLoadingMessage,
    setInputBuffer,
    sendMessage,
    scrollChatListBottom,
    scrollChatListTop,
  }: PromptDropdownProps) => {
    const { selectedPrompt, setSelectedPrompt, autoScrollBack } = useGlobal();
    const [isEditing, setIsEditing] = useState(false);

    const getPromptFromLabel = useCallback(
      (label: string): OptionButtonsPrompt | undefined => {
        const labelToPromptMap: Record<string, OptionButtonsPrompt> = {
          Bullets: OptionButtonsPrompt.Bulletize,
          Expand: OptionButtonsPrompt.Expand,
          Sentence: OptionButtonsPrompt.Sentence,
          Professional: OptionButtonsPrompt.Professional,
          Educator: OptionButtonsPrompt.Educator,
          Friendly: OptionButtonsPrompt.Friendly,
          Neutral: OptionButtonsPrompt.Neutral,
        };
        return labelToPromptMap[label];
      },
      [],
    );

    const dropdownOptions = [
      "Bullets",
      "Expand",
      "Sentence",
      "TONE",
      "Professional",
      "Educator",
      "Friendly",
      "Neutral",
    ];

    const sendEditRequest = useCallback(
      async (label: string) => {
        const systemPrompt = getPromptFromLabel(label);
        if (!systemPrompt) {
          // biome-ignore lint/suspicious/noConsole: <explanation>
          console.error("Invalid label for edit:", label);
          return;
        }

        scrollChatListTop?.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });

        setIsEditing(true);
        setIsLoadingMessage(true);

        const responseStartTime = new Date();
        const lastMessageIndex = chat.length - 1;
        const originalAssistantResponse =
          chat[lastMessageIndex]?.assistant_response;

        if (lastMessageIndex < 0 || !originalAssistantResponse) {
          toast.error("Cannot edit message.");
          setIsEditing(false);
          setIsLoadingMessage(false);
          return;
        }

        setChat((previousChat) => {
          const currentLastIndex = previousChat.length - 1;
          if (currentLastIndex >= 0) {
            const updatedLastItem = {
              ...previousChat[currentLastIndex],
              edited_response: "",
              edited_stats: null,
              edited_reasoning: "",
              edited_tool_calls: [],
              edited_response_duration: null,
            };
            return [
              ...previousChat.slice(0, currentLastIndex),
              updatedLastItem,
            ];
          }
          return previousChat;
        });

        try {
          const result = await sendMessage({
            modifeSystemPrompt: systemPrompt,
            chatHistory: [],
            setChatHistory: setChat,
            user_prompt: originalAssistantResponse,
            isEdit: true,
            setInputBuffer,
            scrollChatListBottom,
            setIsLoadingMessage: setIsLoadingMessage,
          });

          if (!result?.error) {
            const duration = (
              (Date.now() - responseStartTime.getTime()) /
              1000
            ).toFixed(2);
            setChat((previousChat) => {
              const currentLastIndex = previousChat.length - 1;
              if (currentLastIndex >= 0) {
                const updatedLastItem = {
                  ...previousChat[currentLastIndex],
                  edited_response_duration: duration,
                };
                return [
                  ...previousChat.slice(0, currentLastIndex),
                  updatedLastItem,
                ];
              }
              return previousChat;
            });

            if (autoScrollBack) {
              requestAnimationFrame(() => {
                scrollChatListBottom.current?.scrollIntoView({
                  behavior: "smooth",
                  block: "end",
                });
              });
            }
          }

          // biome-ignore lint/suspicious/noExplicitAny: <explanation>
        } catch (error: any) {
          // biome-ignore lint/suspicious/noConsole: <explanation>
          console.error("Error during edit request:", error);
          toast.error(error.message || "Failed to edit message.");
        } finally {
          setIsEditing(false);
          setInputBuffer("");
        }
      },
      [
        chat,
        getPromptFromLabel,
        sendMessage,
        setChat,
        setInputBuffer,
        setIsLoadingMessage,
        scrollChatListBottom,
        scrollChatListTop,
        autoScrollBack,
        setSelectedPrompt,
      ],
    );

    const handleSelectChange = useCallback(
      (selectedLabel: string) => {
        if (selectedLabel === selectedPrompt) {
          setChat((previousChat) => {
            const lastItemIndex = previousChat.length - 1;
            if (lastItemIndex >= 0) {
              const updatedLastItem = {
                ...previousChat[lastItemIndex],
                edited_response: "",
                edited_stats: null,
                edited_reasoning: "",
                edited_tool_calls: [],
                edited_response_duration: null,
              };
              return [...previousChat.slice(0, lastItemIndex), updatedLastItem];
            }
            return previousChat;
          });
          setSelectedPrompt(undefined);
        } else {
          setSelectedPrompt(selectedLabel);
          sendEditRequest(selectedLabel);
        }
      },
      [selectedPrompt, setSelectedPrompt, setChat, sendEditRequest],
    );

    return (
      <DropdownMenu>
        <DropdownMenuTrigger
          asChild
          className="cursor-pointer"
          disabled={isEditing}
        >
          <Button variant={"ghost"} className="p-2 relative">
            {isEditing ? (
              <Icon
                icon="dots-loading"
                className="w-4 h-4 fill-primaryaccent"
              />
            ) : (
              <SlidersHorizontal
                className="hover:text-foreground text-primaryaccent"
                size={16}
              />
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className={cn("py-2", dropdownContentStyles)}>
          {dropdownOptions.map((option) => {
            const isLabel = option === "TONE";
            const isSelected = option === selectedPrompt;

            return (
              <DropdownMenuItem
                key={option}
                className={cn(
                  "gap-2 pr-6",
                  dropdownAndSelectContentItemStyles,
                  isLabel &&
                    "text-xs text-muted-foreground cursor-default py-1 px-3 font-semibold",
                  !isLabel && "cursor-pointer",
                )}
                onClick={() => !isLabel && handleSelectChange(option)}
                disabled={isLabel || isEditing}
                aria-selected={!isLabel && isSelected}
              >
                {!isLabel &&
                  (isSelected ? (
                    <Check size={16} className="text-primaryaccent" />
                  ) : (
                    <div className="size-[16px]" />
                  ))}
                <span className={cn(isLabel ? "pl-1" : "pl-0")}>{option}</span>
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  },
);

PromptDropdown.displayName = "PromptDropdown";

export default PromptDropdown;
